import { useUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import { DateRangePicker, type IDateRangePickerProps } from "@tenant/ui";
import dayjs from "dayjs";
import defaultStyles from "./MonthAndDatePicker.module.scss";

export enum MonthAndDatePickerType {
  Horizontal,
  Column,
  Group,
}

export interface IMonthAndDatePickerProps
  extends Pick<
    IDateRangePickerProps,
    | "className"
    | "currency"
    | "endDate"
    | "endDateLabel"
    // focusedMonthIndex must be given in order to make columnView focus on an arbitrary month
    | "focusedMonthIndex"
    | "isMissingDate"
    | "maxAllowedDate"
    | "minAllowedDate"
    | "months"
    | "pickerType"
    | "preventSameDateSelection"
    | "isDateDisabled"
    | "priceTags"
    | "priceTagsElement"
    | "setEndDate"
    // setFocusedMonthIndex must be given for working with the groupView + columnView combination
    | "setFocusedMonthIndex"
    | "setStartDate"
    | "startDate"
    | "startDateLabel"
    | "a11yModeEnabled"
  > {
  viewType: MonthAndDatePickerType;
  header?: JSX.Element | string;
  showPriceTags?: boolean;
}

export const MonthAndDatePicker = (props: IMonthAndDatePickerProps) => {
  const {
    pickerType,
    startDate,
    endDate,
    startDateLabel,
    endDateLabel,
    months,
    priceTags,
    currency,
    setStartDate,
    setEndDate,
    viewType,
    header,
    focusedMonthIndex,
    setFocusedMonthIndex,
    className,
    isMissingDate = false,
    showPriceTags = false,
    priceTagsElement,
    preventSameDateSelection,
    isDateDisabled,
    maxAllowedDate = dayjs().add(1, "year").toDate(),
    minAllowedDate = dayjs().toDate(),
    a11yModeEnabled = false,
  } = props;

  const styles = useUiStyles(
    CoreUiComponents.MonthAndDatePicker,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreUiComponents.MonthAndDatePicker);

  return (
    <>
      {header && (
        <div className={cn("header-container")}>
          <h2 className={cn("header-title")}>{header}</h2>
        </div>
      )}
      <DateRangePicker
        preventSameDateSelection={preventSameDateSelection}
        className={className}
        minAllowedDate={minAllowedDate}
        maxAllowedDate={maxAllowedDate}
        months={months}
        currency={currency ?? ""}
        startDate={startDate}
        endDate={endDate}
        startDateLabel={startDateLabel}
        endDateLabel={endDateLabel}
        isMissingDate={isMissingDate}
        priceTagsElement={priceTagsElement}
        showPriceTags={showPriceTags}
        priceTags={priceTags}
        isDateDisabled={isDateDisabled}
        a11yModeEnabled={a11yModeEnabled}
        {...(viewType === MonthAndDatePickerType.Horizontal ||
        viewType === MonthAndDatePickerType.Column
          ? { setStartDate, setEndDate, pickerType }
          : undefined)}
        {...(viewType === MonthAndDatePickerType.Column
          ? { focusedMonthIndex, columnView: true }
          : undefined)}
        {...(viewType === MonthAndDatePickerType.Group
          ? { setFocusedMonthIndex, groupView: true }
          : undefined)}
      />
    </>
  );
};
